import { Button, Segmented, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type {
    DataStatisticsVo,
    ProductConsignmentVo,
    ProductStaticDataVo,
    ProductTrendVo,
    SalesRegionVo,
    VisitorRegionVo,
    ReceiveRegionTopVo,
    PriceSalesDistributionVo,
    PurchaseRankingVo
} from "@zhihaoscm/entity";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type FocusEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { consignmentService } from "@/api/services/consignmentService";
import { dashboardService } from "@/api/services/dashboardService";
import { COMMODITY_PRICE_TYPE } from "@zhihaoscm/meta";
import { formatNumberWithUnitAndChinese, formartTimeWithUnitAndChinese } from "@zhihaoscm/utils";
import { AdminTableEmpty } from "@/views/components/TableEmpty";
import { DateRangePicker as ZhDateRangePicker, Grid as ZhGrid, type TableColumnProps } from "zhihao-ui-react";

import AudienceProfilePanel from "./AudienceProfilePanel";
import { ConsignmentPriceDistributionChart, ConsignmentTrendChart } from "./ConsignmentGoodsAnalysisCharts";
import UserGoodsStatisticsDialog, {
    loadUserGoodsStatisticsByProductId
} from "../components/UserGoodsStatisticsDialog";
import UserGoodsStatisticsForVisitorDialog, {
    loadUserGoodsVisitorStatisticsByProductId
} from "../components/UserGoodsStatisticsForVisitorDialog";
import { rememberGoodsOperationTopState } from "../goodsOperationAnalysis/topState";
import CollectorsTableDialog from "../goodsOperationAnalysis/CollectorsTableDialog";
import DataCard from "@/views/components/DataCard/DataCard";
import "./ConsignmentGoodsAnalysisDetailPage.scss";

type DetailRecord = Partial<ProductConsignmentVo["productConsignment"]> & {
    mainFile?: { path?: string };
    productLabel?: string[];
    productTypeName?: string;
    supplierEnterprise?: { name?: string };
};

export interface ConsignmentGoodsAnalysisReceiveRegionParams {
    productId: string;
    sortType: number;
    beginTime?: string;
    endTime?: string;
}

export interface ConsignmentGoodsAnalysisDetailPageProps {
    productId?: string;
    loadDetail?: (
        id: string
    ) => Promise<ProductConsignmentVo | { productConsignment?: DetailRecord; mainFile?: { path?: string } }>;
    loadStatic?: (params: { productId: string; beginTime?: string; endTime?: string }) => Promise<ProductStaticDataVo>;
    loadTrend?: (params: {
        productId: string;
        beginTime?: string;
        endTime?: string;
        timeGranularity: number;
    }) => Promise<ProductTrendVo>;
    loadTop?: (id: string) => Promise<unknown>;
    loadCancelTop?: (id: string) => Promise<unknown>;
    loadVisitorRegion?: (params: {
        productId: string;
        areaLevel: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<VisitorRegionVo[]>;
    loadSalesRegion?: (params: {
        productId: string;
        areaLevel: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<SalesRegionVo[]>;
    loadReceiveRegion?: (params: ConsignmentGoodsAnalysisReceiveRegionParams) => Promise<ReceiveRegionTopVo[]>;
    loadAllTime?: () => Promise<{ beginTime: string; endTime: string }>;
    loadPurchaseRanking?: (params: {
        productId: string;
        beginTime?: string;
        endTime?: string;
        sortType: number;
    }) => Promise<PurchaseRankingVo[]>;
    top?: (id: string) => Promise<unknown>;
    cancelTop?: (id: string) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 商品详情接口，保持商品 ID 位于 /vo/{id} 路径。 */
export async function loadConsignmentGoodsAnalysisDetail(id: string) {
    return consignmentService.getDetail(id);
}

/** 复用 Vue 商品详情静态统计接口。 */
export async function loadConsignmentGoodsAnalysisStatic(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getProductAnalysisStaticData(params);
}

/** 复用 Vue 商品详情趋势接口，并保持时间粒度参数名。 */
export async function loadConsignmentGoodsAnalysisTrend(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
    timeGranularity: number;
}) {
    return dashboardService.getProductAnalysisTrendData(params);
}

/** 复用 Vue 商品详情受众画像的访客地域接口。 */
export async function loadConsignmentGoodsAnalysisVisitorRegion(params: {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getVisitorRegion(params);
}

/** 复用 Vue 商品详情受众画像的销售地域接口。 */
export async function loadConsignmentGoodsAnalysisSalesRegion(params: {
    productId: string;
    areaLevel: number;
    beginTime?: string;
    endTime?: string;
}) {
    return dashboardService.getSalesRegion(params);
}

/** 复用 Vue 商品详情受众画像的收货地排名接口。 */
export async function loadConsignmentGoodsAnalysisReceiveRegion(params: ConsignmentGoodsAnalysisReceiveRegionParams) {
    return dashboardService.getReceiveRegionTop5(params);
}

/** 复用 Vue 商品详情“全部”按钮的完整历史时间范围接口。 */
export async function loadConsignmentGoodsAnalysisAllTime() {
    return dashboardService.getAllTime();
}

/** 复用 Vue 商品详情采购排名 TOP5 接口，保持排序参数和时间范围契约。 */
export async function loadConsignmentGoodsAnalysisPurchaseRanking(params: {
    productId: string;
    beginTime?: string;
    endTime?: string;
    sortType: number;
}) {
    return dashboardService.getProductAnalysisPurchaseTop5(params);
}

/** 统计接口沿用 Vue 的 ISO 日期参数，使用 T 分隔日期和时间。 */
const formatDateTime = (value: string) => `${value}T00:00:00`;

/** 只接受日期控件展示格式，避免失焦时把半成品或非法日期写回查询状态。 */
function normalizeManualDate(value: string): string | null {
    const normalized = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
    const parsed = dayjs(normalized);
    return parsed.isValid() && parsed.format("YYYY-MM-DD") === normalized ? normalized : null;
}

/** 对齐旧 Vue 价格销量分布的数值展示，空值只回退为占位符。 */
const formatAnalysisNumber = (value: number | null | undefined) =>
    value === null || value === undefined ? "--" : Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

/** 将旧版 DataCard 的数字格式转换为 React 卡片可复用的分段数据。 */
const formatAnalysisCard = (value: number | undefined, unit?: string) =>
    formatNumberWithUnitAndChinese(Number(value) || 0, unit);

/** 保持旧版停留时长卡片的“< 1 秒”和中文时分秒展示语义。 */
const formatAnalysisDurationCard = (value: number | undefined) => {
    const normalizedValue = Number.isFinite(value) ? Number(value) : 0;
    if (normalizedValue > 0 && normalizedValue < 1) {
        return [{ label: "<" }, { value: 1, label: "秒" }];
    }
    const formatted = formartTimeWithUnitAndChinese(normalizedValue);
    return formatted.impreciseTokens.length
        ? formatted.impreciseTokens.map(item => ({ value: item.value, label: item.label }))
        : [{ value: 0 }];
};

/** 按旧 Vue 的周定义计算自然周，保持跨年时起止年份分别显示。 */
function getWeekOfYearManual(dateStr: string): number {
    const targetDate = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(targetDate.getTime())) return 0;
    const year = targetDate.getFullYear();
    const firstDayOfYear = new Date(year, 0, 1);
    const dayDiff = Math.floor((Number(targetDate) - Number(firstDayOfYear)) / (1000 * 60 * 60 * 24));
    const firstDayWeekday = firstDayOfYear.getDay();
    const adjustFirstDay = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
    return Math.floor((dayDiff + adjustFirstDay) / 7) + 1;
}

/** 趋势粒度标签与 Vue 页面一致，避免仅显示日期范围造成语义丢失。 */
function formatTrendDateLabel(granularity: number, startDate: string, endDate: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (granularity === 2) {
        return `${start.year()}年第${getWeekOfYearManual(startDate)}周~${end.year()}年第${getWeekOfYearManual(endDate)}周`;
    }
    if (granularity === 3) return `${start.year()}年${start.month() + 1}月~${end.year()}年${end.month() + 1}月`;
    if (granularity === 4) return `${start.year()}年~${end.year()}年`;
    return `${startDate} 至 ${endDate}`;
}

const purchaseRankingColumns: TableColumnProps<PurchaseRankingVo>[] = [
    { label: "排名", minWidth: 135, prop: "rank", render: ({ rowIndex }) => rowIndex + 1 },
    { label: "用户名称", minWidth: 250, prop: "purchaserName" },
    { label: "订单量", minWidth: 200, prop: "orderCount", render: ({ row }) => formatAnalysisNumber(row.orderCount) },
    {
        label: "下单吨数(吨)",
        minWidth: 130,
        prop: "purchaseTon",
        render: ({ row }) => formatAnalysisNumber(row.purchaseTon)
    }
];

/** 商品运营分析详情，保留 Vue 的数据概览、受众画像和置顶操作。 */
export function ConsignmentGoodsAnalysisDetailPage({
    productId,
    loadDetail = loadConsignmentGoodsAnalysisDetail,
    loadStatic = loadConsignmentGoodsAnalysisStatic,
    loadTrend = loadConsignmentGoodsAnalysisTrend,
    loadVisitorRegion = loadConsignmentGoodsAnalysisVisitorRegion,
    loadSalesRegion = loadConsignmentGoodsAnalysisSalesRegion,
    loadReceiveRegion = loadConsignmentGoodsAnalysisReceiveRegion,
    loadAllTime = loadConsignmentGoodsAnalysisAllTime,
    loadPurchaseRanking = loadConsignmentGoodsAnalysisPurchaseRanking,
    top = id => consignmentService.top(id),
    cancelTop = id => consignmentService.cancelTop(id),
    permissions
}: ConsignmentGoodsAnalysisDetailPageProps) {
    const routeParams = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const resolvedId = productId || routeParams.id || new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<DetailRecord>();
    const [staticData, setStaticData] = useState<Partial<ProductStaticDataVo>>({});
    const [trend, setTrend] = useState<ProductTrendVo["trendDataList"]>([]);
    const [purchaseRanking, setPurchaseRanking] = useState<PurchaseRankingVo[]>([]);
    const [purchaseSortType, setPurchaseSortType] = useState(2);
    const [tab, setTab] = useState<"overview" | "audience">("overview");
    const [timeGranularity, setTimeGranularity] = useState(1);
    const [startDate, setStartDate] = useState(() => dayjs().subtract(1, "month").add(1, "day").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(() => dayjs().format("YYYY-MM-DD"));
    const [errorMessage, setErrorMessage] = useState("");
    const [operating, setOperating] = useState(false);
    const [userStatisticsOpen, setUserStatisticsOpen] = useState(false);
    const [visitorStatisticsOpen, setVisitorStatisticsOpen] = useState(false);
    const [collectorsOpen, setCollectorsOpen] = useState(false);
    // 显式传入的空权限列表表示当前用户没有商品运营权限；未传入时继续交由路由权限门禁处理。
    const canOperate = permissions === undefined || permissions.includes("ROLE_SAND_PRODUCT_BI_R");
    const statistics = staticData.dataStatistics as Partial<DataStatisticsVo> | undefined;
    const priceSalesDistribution = staticData.priceSalesDistribution as Partial<PriceSalesDistributionVo> | undefined;
    const distributionRows = priceSalesDistribution?.distributionList || [];
    const product = detail;

    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        void loadDetail(resolvedId)
            .then(result => {
                if (disposed) return;
                const record =
                    (result as { productConsignment?: DetailRecord; mainFile?: { path?: string } })
                        .productConsignment || result;
                const mainFile = (result as { mainFile?: { path?: string } }).mainFile;
                setDetail({ ...record, mainFile: record.mainFile || mainFile });
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("商品分析详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedId]);

    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        const params = { productId: resolvedId, beginTime: formatDateTime(startDate), endTime: `${endDate}T23:59:59` };
        void Promise.all([loadStatic(params), loadTrend({ ...params, timeGranularity })])
            .then(([summary, trendResult]) => {
                if (disposed) return;
                setStaticData(summary || {});
                setTrend(trendResult?.trendDataList || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("商品分析统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [endDate, loadStatic, loadTrend, resolvedId, startDate, timeGranularity]);

    // 采购排名接口独立处理，避免该非核心卡片失败时清空旧 Vue 的统计与趋势数据。
    useEffect(() => {
        if (!resolvedId) return;
        let disposed = false;
        const params = { productId: resolvedId, beginTime: formatDateTime(startDate), endTime: `${endDate}T23:59:59` };
        void loadPurchaseRanking({ ...params, sortType: purchaseSortType })
            .then(result => {
                if (!disposed) setPurchaseRanking(result || []);
            })
            .catch(() => {
                if (!disposed) setPurchaseRanking([]);
            });
        return () => {
            disposed = true;
        };
    }, [endDate, loadPurchaseRanking, purchaseSortType, resolvedId, startDate]);

    const dateLabel = useMemo(
        () => formatTrendDateLabel(timeGranularity, startDate, endDate),
        [endDate, startDate, timeGranularity]
    );

    /** 统一用可键盘访问的按钮承载统计卡，并为读屏提供对应的明细意图。 */
    const actionCard = (label: string, onClick: () => void, card: ReactNode) => (
        <button className="data-card-action" type="button" aria-label={label} onClick={onClick}>
            {card}
        </button>
    );

    const detailQuery = {
        productId: resolvedId,
        beginTime: formatDateTime(startDate),
        endTime: `${endDate}T23:59:59`
    };

    const handleTop = async () => {
        if (!resolvedId || !canOperate || operating) return;
        setOperating(true);
        try {
            if (product?.isTop) await cancelTop(resolvedId);
            else await top(resolvedId);
            const nextIsTop = product?.isTop ? 0 : 1;
            setDetail(current => (current ? { ...current, isTop: nextIsTop } : current));
            rememberGoodsOperationTopState({ productId: resolvedId, isTop: nextIsTop });
        } catch {
            setErrorMessage("商品置顶操作失败");
        } finally {
            setOperating(false);
        }
    };

    /** 点击“全部”时沿用 Vue 接口返回的历史起止日期，而不是伪造固定近一年范围。 */
    const handleSelectAllTime = async () => {
        try {
            const allTime = await loadAllTime();
            setStartDate(dayjs(allTime.beginTime).format("YYYY-MM-DD"));
            setEndDate(dayjs(allTime.endTime).format("YYYY-MM-DD"));
        } catch {
            setErrorMessage("商品分析时间范围加载失败");
        }
    };

    /** zhihao DateRangePicker 允许直接编辑输入框，但手动编辑不会触发完整范围 onChange。 */
    const handleDateRangeBlur = (event: FocusEvent<HTMLDivElement>) => {
        const [nextStart, nextEnd] = Array.from(event.currentTarget.querySelectorAll<HTMLInputElement>("input"))
            .slice(0, 2)
            .map(input => normalizeManualDate(input.value));
        if (!nextStart || !nextEnd || nextStart > nextEnd) return;
        setStartDate(nextStart);
        setEndDate(nextEnd);
    };

    const soldTon = Number(product?.orderedTon || 0);
    const totalTon = Number(product?.initInventoryQuantity || 0);
    const soldPercentage = totalTon > 0 ? Math.ceil((soldTon / totalTon) * 100) : 0;
    const totalSalesAmount = formatAnalysisCard(statistics?.totalSalesAmount, "元");
    const settlementAmount = formatAnalysisCard(statistics?.settlementAmount, "元");
    const refundAmount = formatAnalysisCard(statistics?.refundAmount, "元");
    const totalSalesVolume = formatAnalysisCard(statistics?.totalSalesVolume);
    const actualSettledTons = formatAnalysisCard(statistics?.actualSettledTons);
    const generateOrderCount = formatAnalysisCard(statistics?.generateOrderCount);
    const averagePrice = formatAnalysisCard(statistics?.averagePrice, "元");
    const productVisitorCount = formatAnalysisCard(statistics?.productVisitorCount);
    const productVisitCount = formatAnalysisCard(statistics?.productVisitCount);
    const avgUserStayTime = formatAnalysisDurationCard(statistics?.avgUserStayTime);
    const avgSingleStayTime = formatAnalysisDurationCard(statistics?.avgSingleStayTime);

    return (
        <section aria-label="consignment-goods-analysis-detail-page" className="consignment-goods-analysis-detail-page">
            {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
            <div className="goods-analysis-container">
                <div className="title">商品运营分析详情</div>
                <div className="info">
                    <div className="label">{product?.state === 1 ? "上架" : "下架"}</div>
                    <div className="line" />
                    <div className="label">{product?.isTop ? "已置顶" : "未置顶"}</div>
                </div>
            </div>
            <div className="goods-info-panel-container">
                <div className="left-box">
                    {product?.mainFile?.path ? (
                        <img className="goods-image" src={product.mainFile.path} alt="商品主图" />
                    ) : (
                        <div className="goods-image image-load-fallback" role="img" aria-label="商品主图加载失败">
                            加载失败
                        </div>
                    )}
                    <ZhGrid className="detail-info" cols={1} gap={[0, 0]}>
                        <div className="goods-title">{product?.title || "--"}</div>
                        <div className="tags">
                            {(product?.productLabel || []).map(label => (
                                <div className="tag" key={label}>
                                    {label}
                                </div>
                            ))}
                        </div>
                        {product?.productTypeName ? (
                            <div className="param">
                                <div className="label">品类:</div>
                                <div className="value"> {product.productTypeName}</div>
                            </div>
                        ) : null}
                        {product?.productTypeSpec ? (
                            <div className="param">
                                <div className="label">规格:</div>
                                <div className="value"> {product.productTypeSpec}</div>
                                {/* 保留旧详情页的整段规格文本语义，便于读屏和契约定位。 */}
                                <span className="sr-only">规格: {product.productTypeSpec}</span>
                            </div>
                        ) : null}
                        {product?.supplierEnterprise?.name ? (
                            <div className="param">
                                <div className="label">供应商:</div>
                                <div className="value"> {product.supplierEnterprise.name}</div>
                            </div>
                        ) : null}
                        <div className="param price-param">
                            <div className="label">当前单价:</div>
                            <div className="price-box">
                                <div aria-hidden="true" className="yuan" />
                                <div className="num">
                                    {` ${product?.unitPrice === 0 ? 0 : formatAnalysisNumber(product?.unitPrice)}`}
                                </div>
                                <div className="unit"> 元/吨</div>
                                {product?.priceType ? (
                                    <div className="desc">
                                        ({COMMODITY_PRICE_TYPE.getIntroByCode(product.priceType)})
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="goods-progress-container" aria-label={`商品售卖进度 已售${soldPercentage}%`}>
                            <div
                                className="progress-bar-inner"
                                style={{ width: `${Math.max(0, Math.min(100, soldPercentage))}%` }}
                            >
                                <div className="bar-info">
                                    <div className="percentage">已售{soldPercentage}%</div>
                                    <div className="progress-bar-text">
                                        <div className="count">{formatAnalysisNumber(soldTon)}</div>
                                        <div className="unit">吨</div>
                                    </div>
                                    <div className="symbol">/</div>
                                    <div className="progress-bar-text">
                                        <div className="count">{formatAnalysisNumber(totalTon)}</div>
                                        <div className="unit">吨</div>
                                    </div>
                                </div>
                                <div className="icon" aria-hidden="true">
                                    <i className="iconfont icon-shandian-red" />
                                </div>
                            </div>
                        </div>
                        <div className="param">
                            <div className="label">商品编号:</div>
                            <Link
                                className="value primary"
                                to={`/productManagement/consignment/detail?id=${product?.id || resolvedId}`}
                                state={{ returnTo: `${location.pathname}${location.search}` }}
                            >
                                {` ${product?.id || resolvedId}`}
                            </Link>
                        </div>
                    </ZhGrid>
                </div>
                <div className="right-box">
                    {canOperate ? (
                        <Button type="primary" loading={operating} onClick={handleTop}>
                            {product?.isTop ? "取消置顶" : "置顶该商品"}
                        </Button>
                    ) : (
                        <Typography.Text type="secondary">暂无置顶操作权限</Typography.Text>
                    )}
                    <div className="tips">
                        {product?.isTop
                            ? "取消置顶后会将该商品从商品运营分析列表上取消置顶"
                            : "置顶后会将该商品置顶到商品运营分析列表"}
                    </div>
                </div>
            </div>
            <div className="goods-analysis-tabs-container">
                <div className="inset-tabs" role="tablist" aria-label="商品分析页签">
                    <button
                        className={tab === "overview" ? "active" : ""}
                        role="tab"
                        aria-selected={tab === "overview"}
                        onClick={() => setTab("overview")}
                    >
                        数据概览
                    </button>
                    <button
                        className={tab === "audience" ? "active" : ""}
                        role="tab"
                        aria-selected={tab === "audience"}
                        onClick={() => setTab("audience")}
                    >
                        受众画像
                    </button>
                </div>
                <div className="tab-title">
                    <div className="title">{tab === "overview" ? "数据概览" : "受众画像"}</div>
                    {tab === "overview" ? (
                        <div className="collect-info">
                            <div className="label">商品收藏人数：</div>
                            <button
                                className="count collect-detail-button"
                                type="button"
                                aria-label="查看商品收藏明细"
                                disabled={!statistics?.collectUserCount}
                                onClick={() => setCollectorsOpen(true)}
                            >
                                {statistics?.collectUserCount ?? 0}
                            </button>
                            <div className="unit">人</div>
                        </div>
                    ) : null}
                </div>
                <div className="form-container">
                    <Button type="primary" onClick={() => void handleSelectAllTime()}>
                        全部
                    </Button>
                    {tab === "overview" ? (
                        <div className="granularity-selector" role="radiogroup" aria-label="时间粒度">
                            {[
                                { label: "日", value: 1 },
                                { label: "周", value: 2 },
                                { label: "月", value: 3 },
                                { label: "年", value: 4 }
                            ].map(option => (
                                    <button
                                        key={option.value}
                                        className={timeGranularity === option.value ? "active" : ""}
                                        role="radio"
                                        aria-checked={timeGranularity === option.value}
                                        onClick={() => setTimeGranularity(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <div className="date-picker-wrap" onBlur={handleDateRangeBlur}>
                        <ZhDateRangePicker
                            aria-label="日期范围"
                            format="YYYY-MM-DD"
                            rangeSeparator="-"
                            startPlaceholder="开始日期"
                            endPlaceholder="结束日期"
                            type="daterange"
                            clearable={false}
                            value={[startDate, endDate]}
                            valueFormat="YYYY-MM-DD"
                            onChange={(_value, formattedValue) => {
                                if (!formattedValue) return;
                                // 保持 Vue 日期范围组件的双值更新语义，变更后重新加载统计接口。
                                setStartDate(formattedValue[0] || startDate);
                                setEndDate(formattedValue[1] || endDate);
                            }}
                        />
                    </div>
                    {tab === "overview" && timeGranularity !== 1 ? <div className="date-range">{dateLabel}</div> : null}
                </div>
            </div>
            {tab === "overview" ? (
                <>
                    <div className="data-panel-container">
                        {actionCard(
                            "查看总销售额用户明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard
                                title={`总销售额(${totalSalesAmount.baseUnit}元)`}
                                data={totalSalesAmount.imprecise}
                                tooltipContent={`${totalSalesAmount.precise} 元`}
                            />
                        )}
                        {actionCard(
                            "查看结算金额用户明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard title={`结算金额(${settlementAmount.baseUnit}元)`}>
                                <div className="data-card-content">
                                    <div className="primary-text">{settlementAmount.imprecise}</div>
                                    <div className="bottom-text">
                                        <div className="label">退款金额(元)</div>
                                        <div className="count">{refundAmount.imprecise}</div>
                                        <div className="unit">{refundAmount.baseUnit}元</div>
                                    </div>
                                </div>
                            </DataCard>
                        )}
                        {actionCard(
                            "查看总销量用户明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard title={`总销量(${totalSalesVolume.baseUnit}吨)`}>
                                <div className="data-card-content">
                                    <div className="primary-text">{totalSalesVolume.imprecise}</div>
                                    <div className="bottom-text">
                                        <div className="label">实际结算吨数(吨)</div>
                                        <div className="count">{actualSettledTons.imprecise}</div>
                                        <div className="unit">{actualSettledTons.baseUnit}吨</div>
                                    </div>
                                </div>
                            </DataCard>
                        )}
                        {actionCard(
                            "查看产生订单用户明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard title="产生订单">
                                <div className="data-card-content">
                                    <div className="number-box">
                                        <div className="primary-text">{generateOrderCount.imprecise}</div>
                                        <div className="unit">{generateOrderCount.baseUnit}笔</div>
                                    </div>
                                    <div className="bottom-text">
                                        <div className="label">客单价(元/吨)</div>
                                        <div className="count">{averagePrice.imprecise}</div>
                                        <div className="unit">{averagePrice.baseUnit}元/吨</div>
                                    </div>
                                </div>
                            </DataCard>
                        )}
                        {actionCard(
                            "查看商品访问人数访客明细",
                            () => setVisitorStatisticsOpen(true),
                            <DataCard
                                title="商品访问人数"
                                data={productVisitorCount.imprecise}
                                unit={productVisitorCount.baseUnit}
                                tooltipContent={productVisitorCount.precise}
                            />
                        )}
                        {actionCard(
                            "查看商品访问次数用户明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard
                                title="商品访问次数"
                                data={productVisitCount.imprecise}
                                unit={productVisitCount.baseUnit}
                                tooltipContent={productVisitCount.precise}
                            />
                        )}
                        {actionCard(
                            "查看平均用户停留时长明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard
                                title="平均用户停留时长"
                                data={avgUserStayTime}
                                tooltipContent={
                                    formartTimeWithUnitAndChinese(Number(statistics?.avgUserStayTime) || 0).precise
                                }
                            />
                        )}
                        {actionCard(
                            "查看平均单次停留时长明细",
                            () => setUserStatisticsOpen(true),
                            <DataCard
                                title="平均单次停留时长"
                                data={avgSingleStayTime}
                                tooltipContent={
                                    formartTimeWithUnitAndChinese(Number(statistics?.avgSingleStayTime) || 0).precise
                                }
                            />
                        )}
                    </div>
                    <section className="trend-analysis-chart-container" aria-label="趋势分析">
                        <div className="wrap-header">
                            <div className="left-box">
                                <div className="title">趋势分析</div>
                            </div>
                        </div>
                        <div className="wrap-body">
                            <ConsignmentTrendChart data={trend} />
                        </div>
                    </section>
                    <div className="analysis-secondary-panels">
                        <section className="analysis-panel price-distribution-panel" aria-label="价格销量分布">
                            <div className="analysis-panel-heading">价格销量分布</div>
                            <div className="price-distribution-summary">
                                <span>当前指数价格：</span>
                                <strong>{formatAnalysisNumber(priceSalesDistribution?.currentIndexPrice)}</strong>
                                <span>元/吨</span>
                            </div>
                            <ConsignmentPriceDistributionChart
                                data={distributionRows}
                                totalSalesTon={priceSalesDistribution?.totalSalesTon}
                            />
                        </section>
                        <section className="analysis-panel procurement-ranking-panel" aria-label="采购排名TOP5">
                            <div className="analysis-panel-heading-row">
                                <div className="analysis-panel-heading">采购排名TOP5</div>
                                <Segmented
                                    value={purchaseSortType}
                                    onChange={value => setPurchaseSortType(Number(value))}
                                    options={[
                                        { label: "订单优先", value: 1 },
                                        { label: "销量优先", value: 2 }
                                    ]}
                                />
                            </div>
                            <table aria-label="采购排名TOP5" className="purchase-ranking-table">
                                <thead>
                                    <tr>
                                        {purchaseRankingColumns.map(column => (
                                            <th key={String(column.prop)}>{column.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchaseRanking.length ? (
                                        purchaseRanking.map((row, rowIndex) => (
                                            <tr key={row.customerId}>
                                                <td>{rowIndex + 1}</td>
                                                <td>{row.purchaserName}</td>
                                                <td>{formatAnalysisNumber(row.orderCount)}</td>
                                                <td>{formatAnalysisNumber(row.purchaseTon)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4}>
                                                <AdminTableEmpty />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </section>
                    </div>
                </>
            ) : (
                <AudienceProfilePanel
                    productId={resolvedId}
                    startDate={startDate}
                    endDate={endDate}
                    loadVisitorRegion={loadVisitorRegion}
                    loadSalesRegion={loadSalesRegion}
                    loadReceiveRegion={loadReceiveRegion}
                />
            )}
            {userStatisticsOpen ? (
                <UserGoodsStatisticsDialog
                    open
                    onClose={() => setUserStatisticsOpen(false)}
                    baseParams={detailQuery}
                    loadRows={loadUserGoodsStatisticsByProductId}
                />
            ) : null}
            {visitorStatisticsOpen ? (
                <UserGoodsStatisticsForVisitorDialog
                    open
                    onClose={() => setVisitorStatisticsOpen(false)}
                    baseParams={detailQuery}
                    loadRows={loadUserGoodsVisitorStatisticsByProductId}
                />
            ) : null}
            {collectorsOpen ? (
                <CollectorsTableDialog
                    open
                    productId={resolvedId}
                    title={product?.title}
                    onClose={() => setCollectorsOpen(false)}
                />
            ) : null}
            <div className="operation-btn">
                <Button onClick={() => navigate(-1)}>返回</Button>
            </div>
        </section>
    );
}

export default ConsignmentGoodsAnalysisDetailPage;
